import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '../../../../api/queries/apiGet';
import { API_BASE_URL } from '../constants/consts';
import { authFirewallQueryKeys } from '../constants/queryKeys';
import { EntityDetails, EntityDetailsFull } from '../../types';
import { buildUniversalMfaDomainQueryParams } from '../constants/utils';
import { ActiveUsersAndResourcesMode, QueryFiltersDomainOnly } from '../types';

const END_POINT = '/metric/blockUser';
const PREVIEW_MODE_LIMIT = 9;
const FULL_MODE_LIMIT = 50;

type BlockedUsersDto = EntityDetails;
type BlockedUsersFullDto = Omit<EntityDetailsFull, 'lastAuthTime'> & { lastAuthTime: string };

const convertToBlockedUsers = (data: BlockedUsersDto[]): EntityDetails[] => {
    return data.map((item) => ({
        name: item.name,
        hits: item.hits,
        guid: item.guid
    }));
};

const convertToBlockedUsersFull = (data: BlockedUsersFullDto[]): EntityDetailsFull[] => {
    return data.map((item) => ({
        name: item.name,
        hits: item.hits,
        guid: item.guid,
        lastAuthTime: new Date(item.lastAuthTime),
        riskLevel: item.riskLevel,
        domain: item.domain
    }));
};

const fetchBlockedUsers = async (
    filters: QueryFiltersDomainOnly,
    mode: ActiveUsersAndResourcesMode
): Promise<BlockedUsersDto[] | BlockedUsersFullDto[]> => {
    const data = await apiGet<BlockedUsersDto[]>(`${API_BASE_URL}${END_POINT}`, {
        ...buildUniversalMfaDomainQueryParams(filters),
        limit: mode === 'preview' ? PREVIEW_MODE_LIMIT.toString() : FULL_MODE_LIMIT.toString(),
        mode
    });
    return data;
};

export const useBlockedUsersPreview = (filters: QueryFiltersDomainOnly): UseQueryResult<EntityDetails[]> => {
    return useQuery({
        queryKey: authFirewallQueryKeys.blockedUsers(filters, 'preview'),
        queryFn: () => fetchBlockedUsers(filters, 'preview') as Promise<BlockedUsersDto[]>,
        select: convertToBlockedUsers
    });
};

export const useBlockedUsersFull = (
    filters: QueryFiltersDomainOnly,
    enabled: boolean
): UseQueryResult<EntityDetailsFull[]> => {
    return useQuery({
        queryKey: authFirewallQueryKeys.blockedUsers(filters, 'full'),
        queryFn: () => fetchBlockedUsers(filters, 'full') as Promise<BlockedUsersFullDto[]>,
        select: convertToBlockedUsersFull,
        enabled
    });
};
